import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HeadsetIcon, LeafIcon } from 'lucide-react';
import type { Product, VariantSelection } from '../../types/product';
import { ProductGallery } from './ProductGallery';
import { VariantSelector } from './VariantSelector';
import { StarRating } from './StarRating';
import { StockLabel } from './StockLabel';
import { ProductPerks } from './ProductPerks';
import { StickyBuyBar } from './StickyBuyBar';
import { ProductTabs } from './ProductTabs';
import { RelatedProducts } from './RelatedProducts';
import { Breadcrumbs } from '../layout/Breadcrumbs';
import { useCart } from '../../contexts/CartContext';
import { useInView } from '../../hooks/useInView';
import { formatPrice } from '../../utils/currency';
import { defaultSelection, resolveVariant } from '../../utils/products';

/** Usable kWh × ~300 cycles/yr × €0.25 avg. spread between feed-in and import. */
function estimateSavings(capacityWh: number) {
  return Math.round(capacityWh / 1000 * 0.9 * 300 * 0.25);
}
export function SystemBuilderView({
  product
}: {
  product: Product;
}) {
  const {
    addItem
  } = useCart();
  const [selection, setSelection] = useState<VariantSelection>(() => defaultSelection(product));
  const {
    ref: summaryRef,
    inView
  } = useInView<HTMLDivElement>('-120px 0px 0px 0px');
  const v = resolveVariant(product, selection);
  const groups = product.variants ?? [];
  const savings = estimateSavings(v.capacityWh);
  const payback = savings > 0 ? (v.price / savings).toFixed(1) : '—';
  const add = () => addItem(product, selection);
  return <div className="mx-auto max-w-7xl px-4 pb-24 pt-5 sm:px-6 lg:px-8" data-id="element-449" data-mp="3m7zpjhf7igdr">
      <Breadcrumbs items={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Home batteries',
      href: '/shop/home-batteries'
    }, {
      label: product.name
    }]} data-id="element-450" data-mp="3ha80cv5y58d9" />

      <div className="mt-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between" data-id="element-451" data-mp="dthjfl8ttaws">
        <div data-id="element-452" data-mp="281bqixww5l07">
          <p className="text-sm font-medium text-muted" data-id="element-453" data-mp="280tjr7clroe1">{product.brand} · Build your system</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink sm:text-[32px]" data-id="element-454" data-mp="2d0o9v8ok09y1">{product.name}</h1>
        </div>
        <a href="#details" className="inline-flex items-center gap-2 text-sm" data-id="element-455" data-mp="1ya8l5jwxijv4">
          <StarRating rating={product.rating} data-id="element-456" data-mp="239tqcc2ozoga" />
          <span className="text-muted hover:underline" data-id="element-457" data-mp="238ih3wqh9ohs">
            {product.rating} · {product.reviews.toLocaleString('en-IE')} reviews
          </span>
        </a>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-12 lg:gap-10" data-id="element-458" data-mp="1tcyphmiy5byu">
        {/* Configurator */}
        <div className="lg:col-span-8" data-id="element-459" data-mp="1fdpldl80m8b8">
          <div className="max-w-2xl" data-id="element-460" data-mp="1feqzjykbvax6">
            <ProductGallery images={product.gallery} alt={product.name} activeImage={v.image} data-id="element-461" data-mp="1kapih6mw00q7" />
          </div>

          <ol className="mt-10 space-y-10" data-id="element-462" data-mp="39rgpk57krzza">
            {groups.map((g, i) => <li key={g.id} className="grid gap-4 sm:grid-cols-[2.5rem_1fr]" data-id="element-463" data-mp="3eps1qmlcemko" data-mpi={i}>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-sm font-bold text-white" aria-hidden="true" data-id="element-464" data-mp="131wz6x8nbiif">
                  {i + 1}
                </span>
                <div data-id="element-465" data-mp="1ctj6wjlryzgf">
                  <h2 className="text-lg font-bold tracking-tight text-ink" data-id="element-466" data-mp="1cu2rk82t81y5">{g.name}</h2>
                  <div className="mt-3" data-id="element-467" data-mp="17zyjflht5nvh">
                    <VariantSelector group={g} value={selection[g.id]} onChange={id => setSelection(s => ({
                  ...s,
                  [g.id]: id
                }))} hideLegend={g.kind !== 'swatch'} data-id="element-468" data-mp="1lsee9z0k2flb" />
                  </div>
                </div>
              </li>)}
          </ol>
        </div>

        {/* Sticky summary */}
        <aside className="lg:col-span-4" aria-label="Your system" data-id="element-469" data-mp="1qr7sxurkwcn2">
          <div ref={summaryRef} className="rounded-2xl border border-line bg-white p-5 lg:sticky lg:top-40" data-id="element-470" data-mp="1qpwodsqb70uw">
            <h2 className="text-base font-bold text-ink" data-id="element-471" data-mp="3sj42wzy3lefq">Your system</h2>
            <dl className="mt-4 divide-y divide-line text-sm" data-id="element-472" data-mp="3skynue9dd6jt">
              {groups.map(g => {
              const opt = g.options.find(o => o.id === selection[g.id]) ?? g.options[0];
              return <div key={g.id} className="flex items-start justify-between gap-3 py-2.5" data-id="element-473" data-mp="2on3acep0g712">
                    <dt className="text-muted" data-id="element-474" data-mp="2tiipd9yr88z2">{g.name}</dt>
                    <dd className="text-right" data-id="element-475" data-mp="2xknhu5dguzk4">
                      <span className="block font-medium text-ink" data-id="element-476" data-mp="20vo8en15brqc">{opt.label}</span>
                      {opt.priceDelta > 0 && <span className="text-xs text-muted" data-id="element-477" data-mp="25uizec99omlj">+{formatPrice(opt.priceDelta)}</span>}
                    </dd>
                  </div>;
            })}
            </dl>

            <div className="mt-3 border-t border-line pt-4" data-id="element-478" data-mp="2en2tczem6301">
              <div className="flex items-baseline justify-between" data-id="element-479" data-mp="2eodu0qcriumx">
                <span className="text-sm font-semibold text-ink" data-id="element-480" data-mp="1i455eg74gdob">Total</span>
                <span className="text-right" data-id="element-481" data-mp="1m6jl4rfhuxbd">
                  <AnimatePresence mode="popLayout" initial={false} data-id="element-482" data-mp="1m58kv1l9c7p0">
                    <motion.span key={v.price} initial={{
                    opacity: 0,
                    y: 6
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -6
                  }} transition={{
                    duration: 0.16,
                    ease: [0.23, 1, 0.32, 1]
                  }} className="block text-[28px] font-bold leading-none tracking-tight text-ink" data-id="element-483" data-mp="1eniomq3423xi">
                      {formatPrice(v.price)}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </div>
              {v.compareAt && <p className="mt-1 text-right text-xs" data-id="element-484" data-mp="lzem2dfu66z1">
                  <span className="text-muted line-through" data-id="element-485" data-mp="hx9rnchkd6bc">{formatPrice(v.compareAt)}</span>{' '}
                  <span className="font-semibold text-sale" data-id="element-486" data-mp="cxngd8laq056">You save {formatPrice(v.compareAt - v.price)}</span>
                </p>}
            </div>

            <div className="mt-4 flex items-start gap-3 rounded-xl bg-surface p-3.5" data-id="element-487" data-mp="7zlukfwhjmq0">
              <LeafIcon className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" data-id="element-488" data-mp="3uf155jlr7lnz" />
              <p className="text-xs leading-relaxed text-ink" data-id="element-489" data-mp="3bvyni856ezr">
                Est. <span className="font-bold" data-id="element-490" data-mp="3kj7u64noo08b">{formatPrice(savings)}/year</span> saved with{' '}
                {(v.capacityWh / 1000).toLocaleString('en-IE')} kWh · payback in ~{payback} years
              </p>
            </div>

            <button type="button" onClick={add} className="mt-4 h-12 w-full rounded-lg bg-volt text-sm font-bold text-ink transition-[background-color,transform] duration-150 hover:bg-volt-hover active:scale-[0.98]" data-id="element-491" data-mp="h30tm70kej3r">
              Add system to cart
            </button>
            <div className="mt-3" data-id="element-492" data-mp="c8wgf60pheu8">
              <StockLabel status={product.stock} long data-id="element-493" data-mp="1001y7qq7ih76" />
            </div>
            <a href="tel:+31851234567" className="mt-4 flex items-center gap-2 text-xs font-medium text-ink hover:underline" data-id="element-494" data-mp="zyqt9nl0z3eh">
              <HeadsetIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-495" data-mp="2ph5tb7bcbq6x" />
              Unsure? Our engineers check your setup for free
            </a>
            <div className="mt-5" data-id="element-496" data-mp="2kgi66wq8fthz">
              <ProductPerks data-id="element-497" data-mp="2z78uhusm8agc" />
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-14" data-id="element-498" data-mp="2u8flofo9x7mi">
        <ProductTabs product={product} data-id="element-499" data-mp="2u9guedhthi12" />
      </div>
      <RelatedProducts product={product} data-id="element-500" data-mp="2uary6db9ips6" />

      <div className="lg:hidden" data-id="element-501" data-mp="26i1qwheb0uo8">
        <StickyBuyBar visible={!inView} image={v.image} name={product.name} options={v.labels} price={v.price} compareAt={v.compareAt} ctaLabel="Add to cart" onAdd={add} data-id="element-502" data-mp="26j3aja9dyphk" />
      </div>
    </div>;
}